import { CSSObject } from "@emotion/react";
import {
  constants,
  typographyStyles,
  visuallyHiddenStyles,
} from "@newsela/angelou";
const { colors } = constants;
const { fontFamily, textXSObj, heading5Obj, heading6Obj } = typographyStyles;

const uiNeutral25 = colors.ui.neutral[25];
const uiWhite700 = colors.ui.white[700];
const uiNeutral50 = colors.ui.neutral[50];
const uiNeutral500 = colors.ui.neutral[500];
const uiNeutral600 = colors.ui.neutral[600];
const uiNeutral700 = colors.ui.neutral[700];
const uiCore500 = colors.ui.core[500];

const gridAreas = {
  imageTopLeft: "imageTopLeft",
  imageTopRight: "imageTopRight",
  imageMidLeft: "imageMidLeft",
  imageMidRight: "imageMidRight",
  imageBotLeft: "imageBotLeft",
  imageBotRight: "imageBotRight",
  meta: "meta",
  title: "title",
  subHeader: "subHeader",
  footer: "footer",
} as const;

const cardMinWidth = "246px";
const saveHeight = 42;
const savePadding = "8px";
const textualVerticalPadding = "8px";
const imgBotHeight = 28;
const imgMiddleHeight = 50;
const superTitleHeight = 20;
const textualSidePadding = "12px";
const imageTextualSidePadding = "8px";
export const imageOverlayBorder = "8px";
const borderStyles = `1px solid ${uiNeutral50}`;
const borderRadiusValue = "16px";

export const angelouVisuallyHiddenStyles = visuallyHiddenStyles;

export const wrapperStyles: CSSObject = {
  backgroundColor: uiWhite700,
  overflow: "hidden",
  borderRadius: borderRadiusValue,
  border: `1px solid ${constants.colors.ui.neutral[50]}`,
  height: "100%",
  minWidth: cardMinWidth,
};

export const verticalCardGridStyles: CSSObject = {
  display: "grid",
  gridTemplateAreas: `"${gridAreas.imageTopLeft} ${gridAreas.imageTopRight}"
    "${gridAreas.imageMidLeft} ${gridAreas.imageMidRight}"
    "${gridAreas.imageBotLeft} ${gridAreas.imageBotRight}"
    "${gridAreas.meta} ${gridAreas.meta}"
    "${gridAreas.title} ${gridAreas.title}"
    "${gridAreas.subHeader} ${gridAreas.subHeader}"
    "${gridAreas.footer} ${gridAreas.footer}"
    `,
  gridTemplateRows: `${saveHeight}px ${imgMiddleHeight}px ${imgBotHeight}px ${superTitleHeight}px repeat(2, min-content) 1fr`,
  gridTemplateColumns: "1fr 1fr",
};

export const cardLinkStyle: CSSObject = {
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  padding: 0,

  ":focus": {
    border: `2px solid ${uiCore500}`,
    borderRadius: borderRadiusValue,
  },
};

export const imageSectionStyle: CSSObject = {
  gridArea: `${gridAreas.imageTopLeft} / ${gridAreas.imageTopRight} / ${gridAreas.imageBotLeft} / ${gridAreas.imageBotRight}`,
  gridColumnStart: 1,
  gridColumnEnd: 4,
  gridRowStart: 1,
  gridRowEnd: 4,
};

export const imageSingleGridStyle: CSSObject = {
  display: "flex",

  img: {
    width: "100%",
  },
};

export const imageDoubleGridStyle: CSSObject = {
  display: "flex",
};

export const imageTripleGridStyle: CSSObject = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",

  "img:nth-of-type(1)": {
    height: "50%",
  },

  "img:nth-of-type(2)": {
    height: "50%",
  },

  "img:nth-of-type(3)": {
    height: "100%",
  },
};

export const imageFourGridStyle: CSSObject = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",

  "img:nth-of-type(1)": {
    height: "50%",
  },

  "img:nth-of-type(2)": {
    height: "50%",
  },

  "img:nth-of-type(3)": {
    height: "50%",
  },

  "img:nth-of-type(4)": {
    height: "50%",
  },
};

export const imageVerticalStyle: CSSObject = {
  width: "50%",
  boxSizing: "border-box",
  height: "unset",
};

export const badgeListStyle: CSSObject = {
  display: "flex",
  flexDirection: "column",
  padding: 0,

  "&&&": {
    marginTop: "12px",
  },
};

export const badgeStyle: CSSObject = {
  ...textXSObj,
  margin: "4px 0",
  width: "fit-content",

  "&&&": {
    borderRadius: "0 4px 4px 0 ",
  },
};

export const textualCss: CSSObject = {
  marginLeft: textualSidePadding,
  marginRight: textualSidePadding,
};

export const clearAutoMargin: CSSObject = {
  marginBlock: 0,
  marginInline: 0,
};

export const imgTopLeftStyle: CSSObject = {
  gridArea: gridAreas.imageTopLeft,
};

export const imgBotLeftStyle: CSSObject = {
  gridArea: gridAreas.imageBotLeft,
  height: `${imgBotHeight}px`,
};

export const imgTopRightStyle: CSSObject = {
  gridArea: gridAreas.imageTopRight,
  display: "flex",
  justifyContent: "flex-end",
};

export const imgBotRightStyle: CSSObject = {
  gridArea: gridAreas.imageBotRight,
  display: "flex",
  justifyContent: "flex-end",
};

export const imgOverlayStyles: CSSObject = {
  ...textXSObj,
  fontWeight: 700,
  backgroundColor: uiWhite700,
};

export const labelVerticalStyle: CSSObject = {
  padding: "0 6px",
  margin: 0,
  borderTopRightRadius: imageOverlayBorder,
  backgroundColor: uiWhite700,
  display: "inline-flex",
  placeItems: "center",
  borderTop: borderStyles,
  borderRight: borderStyles,
  height: `${imgBotHeight}px`,
  width: "max-content",
};

export const durationVerticalStyle: CSSObject = {
  backgroundColor: uiWhite700,
  borderTopLeftRadius: imageOverlayBorder,
  display: "flex",
  alignItems: "center",
  gap: "3.5px",
  borderTop: borderStyles,
  borderLeft: borderStyles,
  height: `${imgBotHeight}px`,
  alignSelf: "flex-end",
  paddingLeft: imageTextualSidePadding,
  paddingRight: imageTextualSidePadding,

  svg: {
    width: "12px",
  },
};

export const metaDataStyle: CSSObject = {
  gridArea: gridAreas.meta,
  ...textXSObj,
  color: uiNeutral500,
  display: "flex",
  justifyContent: "space-between",
  padding: `8px ${textualSidePadding} 4px`,
  flexDirection: "row",
};

export const displayDateStyle: CSSObject = {
  ...textXSObj,
};

export const infoIconsStyle: CSSObject = {
  display: "flex",
  justifyContent: "flex-end",
};

export const infoIconsItemStyle: CSSObject = {
  listStyle: "none",
};

export const titleAreaStyle: CSSObject = {
  gridArea: gridAreas.title,
  display: "flex",
  justifyContent: "flex-start",
  flexDirection: "column",
};

export const titleStyle: CSSObject = {
  padding: `8px ${textualSidePadding} 4px ${textualSidePadding}`,
  margin: "8px 0 6px",
  color: uiNeutral700,
  ...heading5Obj,
  ...fontFamily.noto,

  "&&&": {
    lineHeight: "20px",
    marginTop: "10px",
  },
};

export const subHeaderAreaStyles: CSSObject = {
  gridArea: gridAreas.subHeader,
  paddingRight: textualSidePadding,
  paddingLeft: textualSidePadding,
  paddingTop: "6px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
};

export const subHeaderStyles: CSSObject = {
  color: uiNeutral600,
  ...heading6Obj,

  "&&&": {
    lineHeight: "20px",
  },
};

export const saveButtonStyle: CSSObject = {
  borderBottom: borderStyles,
  borderLeft: borderStyles,
  backgroundColor: uiWhite700,
  paddingLeft: savePadding,
  paddingRight: savePadding,
  borderTopLeftRadius: 0,
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: imageOverlayBorder,
  borderTopRightRadius: 0,
  transition: "all 300ms ease-out",
  "@media (prefers-reduced-motion: reduce)": {
    ":hover, :focus": {
      background: `linear-gradient(0deg, rgba(29, 29, 29, 0.08), rgba(29, 29, 29, 0.08)), ${
        uiNeutral25
      }`,
    },
    /* styles to apply if a user's device settings are set to reduced motion */
  },
  "@media (prefers-reduced-motion: no-preference)": {
    ":hover, :focus": {
      background: uiWhite700,
      transform: "scale(1.166)",
      transformOrigin: "top right",
    },
  },
};

export const footerAreaStyle: CSSObject = {
  gridArea: gridAreas.footer,
  alignSelf: "end",
  marginBottom: textualVerticalPadding,
  padding: `4px ${textualSidePadding}`,
};
